// Token bridge for embedded cb2.
//
// Protocol (parent ↔ iframe):
//   1. iframe loads, posts { type: 'cb2-ready' } to parent (window.parent).
//   2. parent posts { type: 'cb2-auth', token: '<Auth0 access token>' } back.
//   3. iframe stores the token; subsequent /api/v1 requests use it.
//   4. parent may post a fresh token at any time (pre-emptive refresh).
//
// On a 401, the iframe posts { type: 'cb2-auth-needed' } to parent so the
// parent can refresh and re-send a token. We never redirect to /login from
// inside the embed.

import { isAllowedParentOrigin } from './embedConfig'

let currentToken = null
let tokenWaiters = []
let parentOrigin = null

const resolveWaiters = (token) => {
  const ws = tokenWaiters
  tokenWaiters = []
  for (const w of ws) w(token)
}

export const setEmbedToken = (token) => {
  currentToken = token
  if (token) resolveWaiters(token)
}

export const getEmbedToken = () => currentToken

// Sync getter compatible with the existing setAccessTokenGetter API.
// Returns the current token; if none yet, waits up to 10s for the parent
// to send one before throwing (so initial render doesn't fire requests
// with no Authorization header).
export const embedTokenGetter = () => {
  if (currentToken) return Promise.resolve(currentToken)
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      tokenWaiters = tokenWaiters.filter((w) => w !== onToken)
      reject(new Error('Embed: parent did not provide an auth token within 10s'))
    }, 10000)
    const onToken = (t) => {
      clearTimeout(timeout)
      resolve(t)
    }
    tokenWaiters.push(onToken)
  })
}

export const postToParent = (msg) => {
  if (!parentOrigin || !window.parent || window.parent === window) return
  window.parent.postMessage(msg, parentOrigin)
}

export const initEmbedAuth = () => {
  window.addEventListener('message', (event) => {
    if (!isAllowedParentOrigin(event.origin)) return
    const data = event.data
    if (!data || typeof data !== 'object') return
    if (data.type === 'cb2-auth' && typeof data.token === 'string') {
      parentOrigin = event.origin
      setEmbedToken(data.token)
    } else if (data.type === 'cb2-ping') {
      parentOrigin = event.origin
      postToParent({ type: 'cb2-pong' })
    }
  })

  // Announce ourselves to whichever origin opened us — we don't yet know
  // which allowed origin it is, so post to '*'. The parent will recognize
  // and respond with cb-auth, locking in parentOrigin for replies.
  if (window.parent && window.parent !== window) {
    window.parent.postMessage({ type: 'cb2-ready' }, '*')
  }
}

export const requestAuthRefresh = () => {
  postToParent({ type: 'cb2-auth-needed' })
}
